import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { US_STATES } from '@/constants/territories/states';
import { getTerritoryColor } from '@/lib/territories';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

import { TerritoryDetailView } from './TerritoryDetailView';
import { TerritoryStatusBadge } from './TerritoryStatusBadge';

interface TerritoryItemProps {
  territory: Territory;
  isEditTab?: boolean;
  onTerritoryClick?: (territory: Territory) => void;
}

export const TerritoryItem = ({
  territory,
  isEditTab = false,
  onTerritoryClick,
}: TerritoryItemProps) => {
  // state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isZipCodesPopoverOpen, setIsZipCodesPopoverOpen] = useState(false);
  const [isMobileZipsExpanded, setIsMobileZipsExpanded] = useState(false);
  const { zorTerritorySettings } = useZorTerritorySettingsStore();
  const { setFocusedTerritory, focusedTerritory } = useTerritoryStore();
  const location = useLocation();

  useEffect(() => {
    if (focusedTerritory && location.pathname == '/dashboard/territories') {
      setIsExpanded(territory.id === focusedTerritory.id);
    }
  }, [focusedTerritory]);

  // event handlers
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTerritoryClick = (territory: Territory) => {
    if (onTerritoryClick) {
      onTerritoryClick(territory);
    } else {
      setFocusedTerritory(territory);
    }
  };

  const handleToggleMobileZips = () => {
    setIsMobileZipsExpanded(!isMobileZipsExpanded);
  };

  const maxFirstLineZips = 5;
  const firstLineZips = territory.zcta_codes.slice(0, maxFirstLineZips);
  const hasMoreZips = territory.zcta_codes.length > maxFirstLineZips;

  return (
    <div>
      <div className="territory-item-row flex items-center justify-between gap-6 border-t border-border px-6 py-3 max-md:flex-col max-md:items-start max-md:gap-3 max-md:p-3">
        <div className="flex flex-col gap-2 lg:flex-1 lg:flex-row lg:items-center">
          <div
            className="as-strong flex cursor-pointer items-center gap-2 border-black text-gray-700 hover:border-b-2"
            onClick={() => handleTerritoryClick(territory)}
          >
            <p>#{territory.id.toString().padStart(5, '0')}</p>
            <p>|</p>
            <p>
              {territory.name ||
                `Territory ${territory.id.toString().padStart(5, '0')}`}
            </p>
          </div>
          <p className="as-body-03 text-muted-foreground max-lg:hidden">|</p>
          <div className="flex items-start gap-1 max-lg:w-full">
            {/* Desktop view */}
            <div className="hidden lg:flex lg:items-start lg:gap-1">
              <p className="as-body-02 as-strong break-words text-gray-700">
                Zips: {firstLineZips.join(', ')}
                {hasMoreZips && '...'}
              </p>
              {hasMoreZips && (
                <Popover
                  open={isZipCodesPopoverOpen}
                  onOpenChange={setIsZipCodesPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <button className="flex flex-shrink-0 items-center justify-center rounded p-0.5 hover:bg-gray-100">
                      <ChevronDown className="h-3 w-3 text-gray-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4">
                    <div className="space-y-2">
                      <h4 className="as-body-02 as-strong text-gray-700">
                        All Zip Codes
                      </h4>
                      <p className="as-body-03 break-words text-gray-600">
                        {territory.zcta_codes.join(', ')}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Mobile view */}
            <div className="flex flex-col gap-1 lg:hidden">
              <div className="flex items-start gap-1">
                <p className="as-body-02 as-strong break-words text-xs text-gray-700">
                  Zips: {firstLineZips.join(', ')}
                  {hasMoreZips && !isMobileZipsExpanded && '...'}
                </p>
                {hasMoreZips && (
                  <button
                    onClick={handleToggleMobileZips}
                    className="flex flex-shrink-0 items-center justify-center rounded p-0.5 hover:bg-gray-100"
                  >
                    {isMobileZipsExpanded ? (
                      <ChevronUp className="h-3 w-3 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-3 w-3 text-gray-500" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          <p className="as-body-03 text-muted-foreground max-lg:hidden">|</p>
          <p className="as-body-02 as-strong text-gray-700 max-lg:text-xs">
            {US_STATES[territory.state as keyof typeof US_STATES]}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 max-md:w-full">
          <div className="flex flex-wrap items-center gap-2">
            {territory.likes_count > 0 && (
              <TerritoryStatusBadge
                count={territory.likes_count}
                label="LIKED"
              />
            )}
            <TerritoryStatusBadge
              label={territory.status}
              color={getTerritoryColor(territory.status, zorTerritorySettings)}
            />
          </div>
          {!isEditTab && (
            <Button
              size="icon"
              variant="outline"
              className="territory-expand-button max-lg:h-8"
              onClick={handleToggleExpanded}
            >
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          )}
        </div>
      </div>
      {isExpanded && !isEditTab && (
        <TerritoryDetailView territory={territory} />
      )}
    </div>
  );
};
